import React, { createContext, useMemo, useState } from 'react';
import { ERROR_MESSAGES } from '../constants';

import type { ErrorsContextProps, IErrorsState } from '../types';

const INITIAL_STATE: IErrorsState = {
  locationError: {
    oneway: { active: false, message: ERROR_MESSAGES.location.oneway },
    pickup: { active: false, message: ERROR_MESSAGES.location.pickup },
    dropoff: { active: false, message: ERROR_MESSAGES.location.dropoff },
  },
  datesError: {
    pickup: { active: false, message: ERROR_MESSAGES.dates.pickup },
    dropoff: { active: false, message: ERROR_MESSAGES.dates.dropoff.emptyField },
  },
  timeError: {
    pickup: { active: false, message: ERROR_MESSAGES.time.pickup },
    dropoff: { active: false, message: ERROR_MESSAGES.time.dropoff },
  },
};

export const ErrorsContext = createContext<ErrorsContextProps>({
  errorsState: INITIAL_STATE,
  updateErrorsState: {} as ErrorsContextProps['updateErrorsState'],
});

const ErrorsProvider = ({ children }: { children: React.ReactNode }) => {
  // ONEWAY DATA
  const [onewayLocationError, setOnewayLocationError] = useState<IErrorsState['locationError']['oneway']>(
    INITIAL_STATE.locationError.oneway,
  );

  // PICKUP DATA
  const [pickupLocationError, setPickupLocationError] = useState<IErrorsState['locationError']['pickup']>(
    INITIAL_STATE.locationError.pickup,
  );
  const [pickupDateError, setPickupDateError] = useState<IErrorsState['datesError']['pickup']>(
    INITIAL_STATE.datesError.pickup,
  );
  const [pickupTimeError, setPickupTimeError] = useState<IErrorsState['timeError']['pickup']>(
    INITIAL_STATE.timeError.pickup,
  );

  // DROPOFF DATA
  const [dropoffLocationError, setDropoffLocationError] = useState<IErrorsState['locationError']['dropoff']>(
    INITIAL_STATE.locationError.dropoff,
  );
  const [dropoffDateError, setDropoffDateError] = useState<IErrorsState['datesError']['dropoff']>(
    INITIAL_STATE.datesError.dropoff,
  );
  const [dropoffTimeError, setDropoffTimeError] = useState<IErrorsState['timeError']['dropoff']>(
    INITIAL_STATE.timeError.dropoff,
  );

  const errorsState: IErrorsState = {
    locationError: { pickup: pickupLocationError, dropoff: dropoffLocationError, oneway: onewayLocationError },
    datesError: { pickup: pickupDateError, dropoff: dropoffDateError },
    timeError: { pickup: pickupTimeError, dropoff: dropoffTimeError },
  };

  const updateErrorsState: ErrorsContextProps['updateErrorsState'] = {
    locationError: {
      pickup: (active: IErrorsState['locationError']['pickup']['active']) =>
        setPickupLocationError((prevState) => ({ ...prevState, active })),
      dropoff: (active: IErrorsState['locationError']['dropoff']['active']) =>
        setDropoffLocationError((prevState) => ({ ...prevState, active })),
      oneway: (active: IErrorsState['locationError']['oneway']['active']) =>
        setOnewayLocationError((prevState) => ({ ...prevState, active })),
    },
    datesError: {
      pickup: (active: IErrorsState['datesError']['pickup']['active']) =>
        setPickupDateError((prevState) => ({ ...prevState, active })),
      dropoff: (
        active: IErrorsState['datesError']['dropoff']['active'],
        message?: IErrorsState['datesError']['dropoff']['message'],
      ) => setDropoffDateError((prevState) => ({ active, message: message ?? prevState.message })),
    },
    timeError: {
      pickup: (active: IErrorsState['timeError']['pickup']['active']) =>
        setPickupTimeError((prevState) => ({ ...prevState, active })),
      dropoff: (active: IErrorsState['timeError']['dropoff']['active']) =>
        setDropoffTimeError((prevState) => ({ ...prevState, active })),
    },
    reset: () => {
      setOnewayLocationError(INITIAL_STATE.locationError.oneway);
      setPickupLocationError(INITIAL_STATE.locationError.pickup);
      setDropoffLocationError(INITIAL_STATE.locationError.dropoff);
      setPickupDateError(INITIAL_STATE.datesError.pickup);
      setDropoffDateError(INITIAL_STATE.datesError.dropoff);
      setPickupTimeError(INITIAL_STATE.timeError.pickup);
      setDropoffTimeError(INITIAL_STATE.timeError.dropoff);
    },
  };

  const props: ErrorsContextProps = useMemo(
    () => ({ errorsState, updateErrorsState }),
    [
      pickupDateError,
      pickupLocationError,
      pickupTimeError,
      dropoffDateError,
      dropoffLocationError,
      dropoffTimeError,
      onewayLocationError,
    ],
  );

  return <ErrorsContext.Provider value={props}>{children}</ErrorsContext.Provider>;
};

export default ErrorsProvider;
